import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { App } from '@/App'
import { FirstBootModePicker } from '@/components/shared/FirstBootModePicker'
import { Toaster } from '@/components/ui/sonner'
import { AuthProvider } from '@/lib/auth/AuthContext'
import { DevRolePreviewProvider } from '@/lib/auth/DevRolePreviewProvider'
import {
  AppEnvError,
  clearStoredPreference,
  getAppEnv,
  isFirstBoot,
  writeStoredPreference,
  type AppMode,
} from '@/lib/env'
import { hydrateThemeFromStorage } from '@/lib/theme/theme-storage'
import './index.css'

// Reforço do anti-FOUC inline em index.html. O snippet inline já aplica o
// data-theme antes do primeiro paint; aqui garantimos que qualquer mudança
// posterior (classe `dark`/`light`, meta theme-color) fique canonizada pelo
// TypeScript source-of-truth em theme-storage.ts.
hydrateThemeFromStorage()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

async function enableMocking(): Promise<void> {
  const env = getAppEnv()
  if (env.mode !== 'mocks') return
  // Import dinâmico mantém todo o código de mocks fora do bundle de produção
  // quando o usuário está em modo `api`. Em prod com modo `mocks` (demo), o
  // chunk lazy é baixado on-demand.
  const { startMocks } = await import('@/mocks/browser')
  await startMocks()
}

function logBootMode(): void {
  const env = getAppEnv()
  const overrideTag = env.hasOverride ? ' (override)' : ''
  const banner =
    env.mode === 'mocks'
      ? `%c[VectraClip] %cMODE=mocks${overrideTag} %c· MSW responde tudo · API real ignorada`
      : `%c[VectraClip] %cMODE=api${overrideTag} %c· conectado a ${env.apiBaseUrl}`
  console.info(
    banner,
    'color:#BF6840;font-weight:600',
    'color:#5FAA72;font-weight:600',
    'color:#A5A199',
  )
}

function renderFatalEnvError(err: AppEnvError): void {
  const root = document.getElementById('root')
  if (!root) {
    console.error('[VectraClip] root not found e env inválida:', err)
    return
  }
  // Botão de fallback expira o override e recarrega — útil quando o usuário
  // forçou modo `api` mas a URL caiu / .env não tem fallback.
  const fallbackHtml = err.canFallbackToMocks
    ? `<button id="vc-fallback-btn" style="margin-top:16px;padding:8px 14px;background:#BF6840;color:#1a1c20;border:0;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer">Voltar para mocks (limpa override)</button>`
    : ''

  root.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#2A2E36;color:#ECE9E2;font-family:ui-sans-serif,system-ui,sans-serif;padding:24px">
      <div style="max-width:560px">
        <div style="font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:#A5A199;margin-bottom:8px">VectraClip · boot error</div>
        <h1 style="margin:0 0 12px;font-size:20px;font-weight:600">${err.message}</h1>
        ${err.help ? `<pre style="margin:0;padding:12px;background:#363A42;border-radius:6px;font-size:12px;white-space:pre-wrap;color:#CFA058;border:1px solid #4A4F58">${err.help}</pre>` : ''}
        <p style="margin-top:16px;font-size:13px;color:#A5A199">Corrija o arquivo <code style="color:#BF6840">.env.local</code> e recarregue.</p>
        ${fallbackHtml}
      </div>
    </div>
  `

  if (err.canFallbackToMocks) {
    document
      .getElementById('vc-fallback-btn')
      ?.addEventListener('click', () => {
        clearStoredPreference()
        window.location.reload()
      })
  }
}

function renderFirstBoot(): void {
  // Tenta detectar o modo recomendado pelo .env. Se a leitura de env falhar,
  // assume `mocks` (mais seguro: sempre funciona em dev).
  let envDefaultMode: AppMode = 'mocks'
  let defaultUrl = ''
  try {
    const env = getAppEnv()
    envDefaultMode = env.mode
    // API VectraClaw dev :3100 — ver .env.api. Produção: .env.production (api-vectraclip…).
    // Porta do Vite (UI) é independente — vite.config.ts server.port (5173).
    defaultUrl = env.apiBaseUrl || 'http://localhost:3100/api'
  } catch (err) {
    console.error('[VectraClip] Falha ao ler ambiente no boot:', err)
    defaultUrl = 'http://localhost:3100/api'
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <FirstBootModePicker
        envDefaultMode={envDefaultMode}
        defaultApiBaseUrl={defaultUrl}
      />
    </React.StrictMode>,
  )
}

function bootstrap(): void {
  if (isFirstBoot()) {
    // Se o .env já configura modo api com URL, persiste automaticamente e pula o picker
    const env = getAppEnv()
    if (env.mode === 'api' && env.apiBaseUrl) {
      writeStoredPreference({ mode: 'api', apiBaseUrl: env.apiBaseUrl })
    } else {
      renderFirstBoot()
      return
    }
  }

  try {
    logBootMode()
  } catch (err) {
    if (err instanceof AppEnvError) {
      renderFatalEnvError(err)
      return
    }
    throw err
  }

  void enableMocking().then(() => {
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            {/* AuthProvider precisa estar DENTRO do BrowserRouter — usa
                useNavigate() para redirect em sessão expirada. */}
            <AuthProvider>
              <DevRolePreviewProvider>
                <App />
                <Toaster />
              </DevRolePreviewProvider>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </React.StrictMode>,
    )
  })
}

bootstrap()
